import { yupResolver } from "@hookform/resolvers/yup";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Button, Col, Label, Row, Spinner } from "reactstrap";
import * as yup from "yup";
import {
  MONTH_OPTIONS,
  STATEMENT_TYPE,
  STATEMENT_TYPE_LABEL,
  YEAR_RANGE_AFTER,
  YEAR_RANGE_BEFORE,
} from "../constants";
import { buildMonthValue, buildYearOptions, readBuyerTaxCode } from "../helper";
import BuyerAutocomplete from "./BuyerAutocomplete";

const schema = yup.object().shape({
  keyword: yup.string(),
  taxCode: yup.string(),
  month: yup.string().required("Vui lòng chọn tháng"),
  year: yup.string().required("Vui lòng chọn năm"),
  type: yup.string().required("Vui lòng chọn hình thức xử lý"),
  invoiceNumbers: yup.string(),
});

const now = new Date();

const defaultValues = {
  keyword: "",
  taxCode: "",
  month: String(now.getMonth() + 1).padStart(2, "0"),
  year: String(now.getFullYear()),
  type: STATEMENT_TYPE.ADJUST,
  invoiceNumbers: "",
};

const yearOptions = buildYearOptions(
  now.getFullYear(),
  YEAR_RANGE_BEFORE,
  YEAR_RANGE_AFTER
);

const labelClass = "text-uppercase fw-bold font-size-11 text-muted";

const FilterBar = props => {
  const { onSearch, isLoading } = props;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const keyword = watch("keyword");
  const type = watch("type");

  // Người bán là chi nhánh/đơn vị đang chọn trên header, không cho sửa ở đây
  const Dashboard = useSelector(state => state.Dashboard);
  const { finalBuInfo } = Dashboard;
  console.log("finalBuInfo", { finalBuInfo });

  // Hai field này không gắn vào input trực tiếp nên phải đăng ký thủ công
  useEffect(() => {
    register("keyword");
    register("type");
  }, [register]);

  // Form giữ tháng và năm tách rời cho dễ chọn, API chỉ nhận "YYYY-MM"
  const submit = values =>
    onSearch({
      ...values,
      month: buildMonthValue(values.year, values.month),
    });

  const titleBenMua =
    finalBuInfo?.value === "ALL_STORE"
      ? "Vui lòng chọn cụ thể đơn vị"
      : `${finalBuInfo?.code} - ${finalBuInfo?.ten}`;

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Row className="gy-3 mb-2">
        <Col md="8">
          <Label className={labelClass}>Bên Bán</Label>
          <input
            type="text"
            className="form-control"
            value={titleBenMua}
            placeholder="Chưa chọn chi nhánh trên thanh trên cùng"
            readOnly
          />
          <small className="text-muted fw-bold">
            MST: {finalBuInfo?.mst || "—"}
          </small>
        </Col>
      </Row>
      <Row className="gy-3">
        <Col md="4">
          {/* Bên mua và MST nằm cùng một dòng, chọn khách hàng thì điền cả hai */}
          <Row className="g-2">
            <Col md="7">
              <Label className={labelClass}>Bên mua</Label>
              <BuyerAutocomplete
                value={keyword}
                onChange={value => setValue("keyword", value)}
                onSelectBuyer={customer =>
                  setValue("taxCode", readBuyerTaxCode(customer))
                }
              />
            </Col>
            <Col md="5">
              <Label className={labelClass}>MST bên mua</Label>
              <input
                type="search"
                className="form-control"
                placeholder="Nhập MST"
                {...register("taxCode")}
              />
            </Col>
          </Row>

          <Label className={`${labelClass} mt-3`}>Danh sách số HĐ</Label>
          <input
            type="search"
            className="form-control"
            placeholder="VD: 97;145;146"
            {...register("invoiceNumbers")}
          />
          <small className="text-muted">Ngăn cách bởi dấu ;</small>
        </Col>

        <Col md="auto">
          <Label className={`${labelClass} d-block`}>Kỳ hóa đơn</Label>
          {/* Giữ dáng một ô như cũ: 2 select không viền nằm trong khung form-control */}
          <div className="form-control d-inline-flex w-auto align-items-center gap-1 py-0 pe-1">
            <select
              className="border-0 bg-transparent py-2"
              style={{ outline: "none" }}
              {...register("month")}
            >
              {MONTH_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="text-muted">/</span>
            <select
              className="border-0 bg-transparent py-2"
              style={{ outline: "none" }}
              {...register("year")}
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <MessageFormValidate
            error={errors.month?.message || errors.year?.message || ""}
          />
        </Col>

        <Col md="auto">
          <Label className={`${labelClass} d-block`}>Hình thức xử lý</Label>
          <div className="btn-group" role="group">
            {[STATEMENT_TYPE.ADJUST, STATEMENT_TYPE.REPLACE].map(item => (
              <Button
                key={item}
                type="button"
                color={type === item ? "primary" : "light"}
                onClick={() => setValue("type", item)}
              >
                {STATEMENT_TYPE_LABEL[item]}
              </Button>
            ))}
          </div>
          <MessageFormValidate error={errors.type?.message || ""} />
        </Col>
      </Row>

      <div className="d-flex gap-2 mt-3">
        <Button
          type="submit"
          color="primary"
          disabled={
            isLoading || !finalBuInfo || finalBuInfo?.value === "ALL_STORE"
          }
        >
          {isLoading ? (
            <Spinner size="sm" className="me-1" />
          ) : (
            <i className="bx bx-search-alt me-1" />
          )}
          Tìm kiếm
        </Button>
        <Button
          type="button"
          color="secondary"
          outline
          disabled={isLoading}
          onClick={() => reset(defaultValues)}
        >
          Xóa lọc
        </Button>
      </div>
    </form>
  );
};

FilterBar.propTypes = {
  onSearch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

FilterBar.defaultProps = {
  isLoading: false,
};

export default FilterBar;
